// code-guardian-ignore-duplicates: shared presentational scaffolding (table/modal/form/report layout from the design system)
import { TableActionGroup, TableActionButton } from '@channelplay/table-actions';
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CalendarClock,
  CheckCircle,
  Clock,
  FileText,
  Info,
  Search,
  X,
  XCircle,
} from 'lucide-react';
import FilterDropdownButton from '../components/FilterDropdownButton';
import MyApprovalsHeader from '../components/MyApprovalsHeader';
import { BlockLoader } from '../components/loading';
import ProbationReviewModal from '../components/probation/ProbationReviewModal';
import { SortTh, useTableSort } from '../components/dashboard/useTableSort';
import { probationAPI } from '../services/api';
import { formatIsoDay } from '../utils/dateHelpers';
import type {
  ProbationDecisionResult,
  ProbationListItem,
  ProbationStatus,
  ProbationStep,
  ProbationView,
} from '../types/probation';

// My Approvals → Probation Confirmation. The list of employees whose probation
// ends within 7 days (or already has) and who are waiting on the signed-in
// user's decision in 1Office — the same queue the Employee Portal shows. The
// decision itself happens in ProbationReviewModal; this page only lists, filters
// and opens. 1Office decides who the approver is from the session, server-side,
// so nothing here sends a user id.

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const VIEW_LABELS: Record<ProbationView, string> = {
  pending: 'Pending',
  all: 'All',
};

// The Pending / All switch is named in the address as ?view=pending or ?view=all, so a
// link can state the list it means. A missing or unknown value is Pending — the list of
// decisions still waiting, and where the page has always opened.
const viewFromAddress = (value: string | null): ProbationView => (value === 'all' ? 'all' : 'pending');

const statusConfig: Record<ProbationStatus, {
  label: string;
  bgColor: string;
  textColor: string;
  icon: typeof Clock;
}> = {
  pending: {
    label: 'Pending',
    bgColor: 'bg-amber-100',
    textColor: 'text-amber-700',
    icon: Clock,
  },
  confirmed: {
    label: 'Confirmed',
    bgColor: 'bg-green-100',
    textColor: 'text-green-700',
    icon: CheckCircle,
  },
  extended: {
    label: 'Extended',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
    icon: CalendarClock,
  },
  terminated: {
    label: 'Terminated',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
    icon: XCircle,
  },
};

const STEP_LABELS: Record<ProbationStep, string> = {
  l1_review: 'Manager review',
  final_review: 'Final review – L1 done',
  view_only: '—',
};

const thCls = 'px-4 py-3 text-xs font-semibold text-gray-600 whitespace-nowrap';

// The API sends plain YYYY-MM-DD calendar dates, shown as "20 Sep 2026" — the
// same format as the review modal and the API's success messages. Read off the
// string, never `new Date(str)`, so no timezone can shift the day.
const formatDate = (value: string | null) => {
  const day = /^\d{4}-\d{2}-\d{2}/.exec(value ?? '')?.[0];
  return day ? formatIsoDay(day) : '—';
};

// "Due" only means something while the decision is still open; a confirmed,
// extended or terminated row in the All view shows a dash.
const renderDue = (row: ProbationListItem) => {
  const days = row.daysToConfirmation;
  if (row.status !== 'pending' || days == null) return <span className="text-sm text-gray-400">—</span>;
  if (days < 0) {
    const n = Math.abs(days);
    return <span className="text-sm font-medium text-red-600 whitespace-nowrap">Overdue by {n} day{n === 1 ? '' : 's'}</span>;
  }
  if (days === 0) return <span className="text-sm font-medium text-amber-700 whitespace-nowrap">Due today</span>;
  return <span className="text-sm text-gray-700 whitespace-nowrap">Due in {days} day{days === 1 ? '' : 's'}</span>;
};

// Surface the server's own message (e.g. IDENTITY_UNRESOLVED asks the user to
// sign in again) rather than a generic one.
const listErrorMessage = (err: unknown): string => {
  const data = (err as { response?: { data?: { message?: string } } })?.response?.data;
  return data?.message || "Couldn't load probation confirmations. Please try again.";
};

// The title the review modal shows after "Employee Confirmation", as the
// Employee Portal does: "<Name>-<EmpCode>".
const employeeLabelOf = (row: ProbationListItem) =>
  row.employeeCode ? `${row.employeeName}-${row.employeeCode}` : row.employeeName;

export default function ProbationConfirmationPage() {
  // Read from the address on every render, never copied into state: the address is the
  // one place that says which list is open, so a link, a refresh and Back all agree.
  const [searchParams, setSearchParams] = useSearchParams();
  const view = viewFromAddress(searchParams.get('view'));
  // Replaces rather than adds a step, so Back leaves the page instead of flipping the
  // switch back; every other value in the query is kept.
  const setView = useCallback((next: ProbationView) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('view', next);
      return params;
    }, { replace: true });
  }, [setSearchParams]);
  const [showViewDropdown, setShowViewDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [items, setItems] = useState<ProbationListItem[]>([]);
  const [submitEnabled, setSubmitEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ tone: 'success' | 'info'; text: string } | null>(null);

  // The row being reviewed, with the view it was opened from — kept apart from
  // `view` so switching Pending/All behind an open modal can't change its mode.
  const [reviewing, setReviewing] = useState<{ row: ProbationListItem; view: ProbationView } | null>(null);

  // Each list read enriches every row from 1Office, so a slow Pending answer can
  // land after a quicker All one. Only the latest request may write state.
  const requestSeqRef = useRef(0);

  const fetchList = useCallback(async (which: ProbationView) => {
    const seq = ++requestSeqRef.current;
    try {
      setLoading(true);
      setError(null);
      const response = await probationAPI.list(which);
      if (seq !== requestSeqRef.current) return;
      if (response.data.success) {
        setItems(response.data.data?.items || []);
        setSubmitEnabled(response.data.data?.submitEnabled !== false);
      } else {
        setItems([]);
        setError(listErrorMessage(null));
      }
    } catch (err) {
      if (seq !== requestSeqRef.current) return;
      console.error('Error fetching probation confirmations:', err);
      setItems([]);
      setError(listErrorMessage(err));
    } finally {
      if (seq === requestSeqRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList(view);
  }, [view, fetchList]);

  const filteredItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    // Base order: probation end date ascending, so the most overdue come first.
    // Rows without a date go last. This is also what the table falls back to
    // when a header's sort is cleared.
    const byEndDate = [...items].sort((a, b) => {
      if (!a.confirmationDate && !b.confirmationDate) return 0;
      if (!a.confirmationDate) return 1;
      if (!b.confirmationDate) return -1;
      return a.confirmationDate.localeCompare(b.confirmationDate);
    });
    if (!query) return byEndDate;
    return byEndDate.filter((row) =>
      [row.employeeName, row.employeeCode, row.projectName, row.projectCode]
        .some((v) => (v ?? '').toLowerCase().includes(query)),
    );
  }, [items, searchQuery]);

  const sort = useTableSort<ProbationListItem>(
    filteredItems,
    {
      employee: (r) => r.employeeName,
      project: (r) => r.projectName,
      designation: (r) => r.designation,
      dateOfJoining: (r) => r.dateOfJoining,
      confirmationDate: (r) => r.confirmationDate,
      status: (r) => statusConfig[r.status]?.label,
    },
    { defaultKey: 'confirmationDate', defaultDir: 'asc' },
  );
  const sortedItems = sort.sorted;

  // Client-side pagination over the filtered+sorted set.
  const totalRecords = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedItems = sortedItems.slice(pageStart, pageStart + pageSize);

  // Reset to the first page whenever the filtered result set changes shape.
  useEffect(() => {
    setCurrentPage(1);
  }, [view, searchQuery, pageSize, sort.sortKey, sort.sortDir]);

  const openReview = (row: ProbationListItem) => {
    setReviewing({ row, view });
  };

  // Plain close (Cancel / Close / ×). The modal may also pass a message when it
  // closed because the request moved on underneath it (already actioned, not
  // this user's step any more, changed since opened) — then the list is stale
  // too, so show why and re-read it. Only a string counts: a handler wired
  // straight to onClick hands over the click event instead.
  const closeReview = (message?: unknown) => {
    setReviewing(null);
    if (typeof message === 'string' && message.trim()) {
      setNotice({ tone: 'info', text: message });
      fetchList(view);
    }
  };

  const handleSubmitted = (result: ProbationDecisionResult) => {
    setReviewing(null);
    setNotice({ tone: 'success', text: result.message });
    fetchList(view);
  };

  const renderStatusBadge = (status: ProbationStatus) => {
    const config = statusConfig[status] ?? statusConfig.pending;
    const StatusIcon = config.icon;
    return (
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${config.bgColor} ${config.textColor}`}
      >
        <StatusIcon className="w-3.5 h-3.5" />
        {config.label}
      </span>
    );
  };

  const renderTable = () => {
    return (
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-100">
                <SortTh label="Employee" sortKey="employee" sort={sort} className={thCls} />
                <SortTh label="Project" sortKey="project" sort={sort} className={thCls} />
                <SortTh label="Designation" sortKey="designation" sort={sort} className={thCls} />
                <SortTh label="Date of Joining" sortKey="dateOfJoining" sort={sort} className={thCls} />
                <SortTh label="Probation end date" sortKey="confirmationDate" sort={sort} className={thCls} />
                <th className={`${thCls} text-left`}>Due</th>
                <th className={`${thCls} text-left`}>Your step</th>
                <SortTh label="Status" sortKey="status" sort={sort} className={thCls} />
                <th className={`${thCls} sticky right-0 z-10 bg-gray-50 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)]`}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedItems.map((row) => (
                <tr key={row.confirmationId} className="group hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900 text-sm whitespace-nowrap">{row.employeeName}</p>
                    {row.employeeCode && <p className="text-xs text-gray-500 font-mono">{row.employeeCode}</p>}
                  </td>
                  {/* min widths: on a phone the table scrolls sideways rather than
                      squeezing these to one word per line under the sticky Actions. */}
                  <td className="px-4 py-3 min-w-[10rem]">
                    <p className="text-sm text-gray-700">{row.projectName || '—'}</p>
                    {row.projectCode && <p className="text-xs text-gray-500 font-mono">{row.projectCode}</p>}
                  </td>
                  <td className="px-4 py-3 min-w-[9rem]">
                    <span className="text-sm text-gray-700">{row.designation || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{formatDate(row.dateOfJoining)}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600 whitespace-nowrap">{formatDate(row.confirmationDate)}</span>
                  </td>
                  <td className="px-4 py-3">{renderDue(row)}</td>
                  <td className="px-4 py-3">
                    {row.yourStep === 'view_only' || !STEP_LABELS[row.yourStep] ? (
                      // The same light dash as an empty Due.
                      <span className="text-sm text-gray-400">—</span>
                    ) : (
                      <span
                        className="text-sm text-gray-700 whitespace-nowrap"
                        title={row.yourStep === 'final_review' && row.l1Feedback ? `L1 feedback: ${row.l1Feedback}` : undefined}
                      >
                        {STEP_LABELS[row.yourStep]}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">{renderStatusBadge(row.status)}</td>
                  <td className="sticky right-0 z-10 bg-white px-4 py-3 text-center shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.1)] group-hover:bg-gray-50">
                    <TableActionGroup>
                      {row.yourStep === 'view_only' ? (
                        <TableActionButton
                          action="view"
                          label={`View ${row.employeeName}'s probation confirmation`}
                          type="button"
                          onClick={() => openReview(row)}
                        />
                      ) : (
                        <TableActionButton
                          action="review"
                          label={`Review ${row.employeeName}'s probation confirmation`}
                          type="button"
                          onClick={() => openReview(row)}
                        />
                      )}
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {totalRecords > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-100 bg-gray-50/40">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border border-gray-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-200"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                Showing {pageStart + 1}–{Math.min(pageStart + pageSize, totalRecords)} of {totalRecords}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-600">Page {safePage} of {totalPages}</span>
                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header: the section's title, then Approvals | Probation Confirmation as tabs,
          this page the open one. */}
      <MyApprovalsHeader subtitle="Employees whose probation ends within 7 days or has already ended, waiting for your decision." />

      {notice && (
        <div
          role="status"
          className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${
            notice.tone === 'success'
              ? 'border-green-200 bg-green-50 text-green-800'
              : 'border-blue-200 bg-blue-50 text-blue-800'
          }`}
        >
          <span className="flex items-start gap-2">
            {notice.tone === 'success'
              ? <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
              : <Info className="mt-0.5 h-4 w-4 shrink-0" />}
            {notice.text}
          </span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 rounded p-0.5 hover:bg-black/5"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        <div>
          {!loading && !error && !submitEnabled && (
            <div className="mb-4 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
              <span>Submitting from ChannelPlex is switched off for now — you can review here and submit in the Employee Portal.</span>
            </div>
          )}

          <div className="mb-6">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by Employee Name, Employee Code or Project..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div className="relative">
                <FilterDropdownButton
                  onClick={() => setShowViewDropdown(!showViewDropdown)}
                  label={VIEW_LABELS[view]}
                />

                {showViewDropdown && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setShowViewDropdown(false)}
                    />
                    <div className="absolute left-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                      {(Object.keys(VIEW_LABELS) as ProbationView[]).map((v) => (
                        <button
                          key={v}
                          onClick={() => {
                            setView(v);
                            setShowViewDropdown(false);
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors ${view === v ? 'bg-gray-50 font-medium' : ''}`}
                        >
                          {VIEW_LABELS[v]}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <BlockLoader label="Loading probation confirmations..." className="text-center py-12 bg-white rounded-xl border border-gray-100" />
          ) : error ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-1">Error loading data</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={() => fetchList(view)}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : sortedItems.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-gray-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              {searchQuery.trim() ? (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No matches</h3>
                  <p className="text-gray-500">Try adjusting your search</p>
                </>
              ) : view === 'pending' ? (
                <h3 className="text-lg font-medium text-gray-900">No probation confirmations are waiting for you.</h3>
              ) : (
                <h3 className="text-lg font-medium text-gray-900">No probation confirmations to show.</h3>
              )}
            </div>
          ) : (
            renderTable()
          )}
        </div>
      </section>

      {reviewing && (
        <ProbationReviewModal
          confirmationId={reviewing.row.confirmationId}
          view={reviewing.view}
          employeeLabel={employeeLabelOf(reviewing.row)}
          onClose={closeReview}
          onSubmitted={handleSubmitted}
        />
      )}
    </div>
  );
}
